import { Suspense, lazy } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import WalkInWizard from './routes/kiosk/WalkInWizard';
import Roster from './routes/Roster';
import DevSwatchesPage from './routes/site/DevSwatchesPage';
import StaffClockIn from './routes/kiosk/StaffClockIn';
import AdminSeed from './routes/kiosk/AdminSeed';
import WaitingView from './routes/kiosk/WaitingView';
import TvDisplay from './routes/tv/TvDisplay';
import HomePage from './routes/site/HomePage';
import AboutPage from './routes/site/AboutPage';
import ServicesPage from './routes/site/ServicesPage';
import { LocaleContext, type Locale } from './content';

/**
 * /book is lazy-loaded on purpose.
 *
 * It is the only marketing route that touches the Firebase SDK (via
 * bookingApi -> firebase/functions). Importing it eagerly pulled ~200 kB
 * of SDK into the bundle every visitor downloads, including people who
 * only ever read the homepage. Splitting keeps the marketing bundle lean
 * and defers the SDK to the moment someone actually books.
 */
const BookPage = lazy(() => import('./routes/site/BookPage'));

/**
 * Surfaces on the same domain:
 *   /              → Marketing site (EN)
 *   /es            → Marketing site (ES) — same component tree, es dictionary
 *   /kiosk         → Locked landscape tablet at the front desk (customer)
 *   /staff         → Barber clock-in screen (PIN gated), own installable app
 *   /kiosk/staff   → redirects to /staff
 *   /kiosk/admin/seed → Owner-only barber seed (owner PIN gated)
 *   /w/:shortId    → Customer's phone view of their place in line
 *   /tv            → Shop floor display (unauthenticated, read-only)
 *   /roster        → PIN-gated "who's on shift today"
 *   /dev/swatches  → Design-token reference (kept on purpose)
 *
 * Locale is a URL prefix, not a toggle: one component tree, two dictionaries.
 * Duplicating pages per language guarantees drift — see docs/COPY_STYLE.md.
 */
function BookFallback() {
  return (
    <div className="min-h-screen bg-page grid place-items-center">
      <p className="font-serif text-navy text-lg" role="status" aria-live="polite">
        …
      </p>
    </div>
  );
}

function MarketingRoutes({ locale }: { locale: Locale }) {
  return (
    <LocaleContext.Provider value={locale}>
      <Routes>
        <Route index element={<HomePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route
          path="book"
          element={
            <Suspense fallback={<BookFallback />}>
              <BookPage />
            </Suspense>
          }
        />
      </Routes>
    </LocaleContext.Provider>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/kiosk" element={<WalkInWizard />} />
      <Route path="/staff" element={<StaffClockIn />} />
      {/* The clock-in screen moved to its own installable app. These keep
          every printed card, bookmark and muscle-memory tap working, and
          replace rather than push so Back does not bounce into the
          redirect. */}
      <Route path="/kiosk/staff" element={<Navigate to="/staff" replace />} />
      <Route path="/kiosk/staff/*" element={<Navigate to="/staff" replace />} />
      <Route path="/kiosk/admin/seed" element={<AdminSeed />} />
      <Route path="/kiosk/waiting/:walkInId" element={<WaitingView />} />
      <Route path="/w/:shortId" element={<WaitingView />} />
      <Route path="/tv" element={<TvDisplay />} />
      <Route path="/roster" element={<Roster />} />
      <Route path="/dev/swatches" element={<DevSwatchesPage />} />
      <Route path="/es/*" element={<MarketingRoutes locale="es" />} />
      <Route path="/*" element={<MarketingRoutes locale="en" />} />
    </Routes>
  );
}
